/**
 * @fileoverview Static content for the illustrated document-builder help panel (Phase 5, Workstream J2).
 * @module ui-admin/help/builderHelpContent
 *
 * Responsibilities: - Author the eight `HelpSection` entries shown in `BuilderHelpPanel`.
 *                   - Each section is structured data (title, summary, HelpBlock[], optional
 *                     illustration) — never JSX — so it can be validated and reused.
 * Dependencies:     - Help type contracts from `./types`.
 * Architecture:     - Pure static data bundled with the app. No API/Dexie reads, no mock data,
 *                     no side effects. Local-first by construction (available offline).
 * Notes:            - Content describes the Phase 2 canvas exactly as built. When the canvas UI
 *                     changes, the matching section + illustration must be updated in the same PR.
 */

import type { HelpSection } from './types';

/**
 * @contract BUILDER_HELP_SECTIONS
 * Purpose: Ordered list of the eight help sections rendered by `BuilderHelpPanel`.
 * Rules: Order is the table-of-contents order. Every `illustration` id must reference a
 *        component registered in `./illustrations`. Section ids are unique.
 */
export const BUILDER_HELP_SECTIONS: HelpSection[] = [
  {
    id: 'overview',
    title: 'Canvas overview',
    summary: 'The three zones of the builder and what each one does.',
    illustration: 'builderShell',
    body: [
      {
        type: 'paragraph',
        text: 'The document builder has three zones. On the left is the configuration panel for page setup, header, the section list, and branding. In the centre is the canvas — the page itself, where you add and order the sections (called bands) that make up your document. On the right is the live preview, which shows exactly what the generated PDF will look like.',
      },
      {
        type: 'paragraph',
        text: 'Across the top of the canvas is a tab strip that switches between the document Body and an optional Cover Page, plus the "Add component" button that opens the component palette.',
      },
      {
        type: 'tip',
        text: 'You can collapse the left panel and resize (or full-screen) the preview to give the canvas more room — your layout preferences are remembered between sessions.',
      },
    ],
  },
  {
    id: 'firstDocument',
    title: 'Building your first document',
    summary: 'The end-to-end workflow from an empty canvas to a published template.',
    illustration: 'builderShell',
    body: [
      {
        type: 'steps',
        items: [
          'Choose the document type when you create the template (letter, notice, report, etc.).',
          'Click "Add component" to open the palette, then add the bands you need — letterhead, recipient block, body text, signature, and so on.',
          'Select a band to configure it in the left panel; drag bands by the grip handle to reorder them.',
          'Watch the live preview update as you build — it is the same renderer that produces the PDF.',
          'Save to keep a draft; Publish when the template is ready to generate real documents.',
        ],
      },
      {
        type: 'tip',
        text: 'Start from the system library if you want a head start — adopt an existing template and customise it instead of building from scratch.',
      },
    ],
  },
  {
    id: 'coverPage',
    title: 'Cover letter / cover page',
    summary: 'Add a full leading page that renders before the document body.',
    illustration: 'coverPageTab',
    body: [
      {
        type: 'paragraph',
        text: 'A cover page is a complete page that renders ahead of the body — useful for a formal cover letter or a title page. Use the "Add Cover Page" button in the canvas header to create one.',
      },
      {
        type: 'paragraph',
        text: 'Once a cover page exists, the header shows Body and Cover Page tabs. Switch between them to build each surface independently. The cover page has full parity with the body — every band type and configuration option is available on both.',
      },
      {
        type: 'tip',
        text: 'The Cover Page renders as the leading page of the generated document, before the first body page.',
      },
    ],
  },
  {
    id: 'mergeFields',
    title: 'Data, merge fields & placeholders',
    summary: 'Insert {{fieldName}} tokens that resolve to real case data at render time.',
    illustration: 'mergeField',
    body: [
      {
        type: 'paragraph',
        text: 'Merge fields are placeholders written as {{fieldName}} that are replaced with real data when the document is generated — for example {{ownerName}} becomes the property owner\u2019s name.',
      },
      {
        type: 'steps',
        items: [
          'In a rich-text band, type {{ to open the merge-field picker.',
          'Choose a field from the autocomplete list; it is inserted as a token.',
          'The preview shows sample values so you can see how the finished document reads.',
        ],
      },
      {
        type: 'warning',
        text: 'If a field has no value for a given case, it resolves to an empty string — keep surrounding punctuation in mind when you write the sentence.',
      },
    ],
  },
  {
    id: 'dynamicComponents',
    title: 'Dynamic components',
    summary: 'Sections that expand with real inspection data when the document is generated.',
    illustration: 'dynamicBand',
    body: [
      {
        type: 'paragraph',
        text: 'A dynamic component is a section that repeats or expands with real data at render time. Dynamic bands are marked with a DYNAMIC badge in the canvas.',
      },
      {
        type: 'paragraph',
        text: 'The dynamic band types are: the violations list (one row per violation), the photo gallery (one image per inspection photo), the metadata grid (key/value case fields), form responses (one row per answered field), and advanced fields (values from a linked inspection template).',
      },
      {
        type: 'tip',
        text: 'Use the "Preview with sample data" toggle on a dynamic band to see how it will expand before you publish.',
      },
    ],
  },
  {
    id: 'previewFidelity',
    title: 'Preview & PDF fidelity',
    summary: 'Why the on-screen preview matches the generated PDF exactly.',
    illustration: 'previewPane',
    body: [
      {
        type: 'paragraph',
        text: 'The preview pane and the generated PDF are produced by the same rendering engine, so what you see in the preview is what you get in the PDF — fonts, spacing, page breaks, and all.',
      },
      {
        type: 'paragraph',
        text: 'The preview has three states: a normal split view beside the canvas, a collapsed strip, and a full-screen overlay. Drag the handle between the canvas and preview to resize it, or drag it fully to go full-screen. Use the zoom control to inspect detail.',
      },
      {
        type: 'tip',
        text: 'If something looks wrong in the preview, it will look the same in the PDF — fix it here rather than after generating.',
      },
    ],
  },
  {
    id: 'branding',
    title: 'Templates, themes & branding',
    summary: 'Apply your department theme, override per document, and reuse templates.',
    body: [
      {
        type: 'paragraph',
        text: 'Your department theme supplies the default colours, fonts, and logo. Individual documents can override these where needed, so a single notice can differ from the department default without changing it for everyone.',
      },
      {
        type: 'paragraph',
        text: 'Save a finished design as a template so it can be reused, and browse the template gallery to adopt designs that already exist.',
      },
      {
        type: 'tip',
        text: 'Set branding once at the department level so every new template starts on-brand automatically.',
      },
    ],
  },
  {
    id: 'shortcuts',
    title: 'Keyboard shortcuts',
    summary: 'Speed up common builder actions with the keyboard.',
    body: [
      { type: 'keyShortcut', keys: 'Esc', description: 'Close the open panel, palette, popover, help, or tour.' },
      { type: 'keyShortcut', keys: 'Enter / Space', description: 'Toggle the focused accordion section open or closed.' },
      { type: 'keyShortcut', keys: '\u2191 / \u2193', description: 'Move focus between accordion section headers in the left panel.' },
      { type: 'keyShortcut', keys: '{{', description: 'Open the merge-field picker inside a rich-text band.' },
      { type: 'tip', text: 'Most controls are reachable with Tab — the builder is fully keyboard-navigable.' },
    ],
  },
];
